import { useState } from 'react';

import { ExternalLink, FilePlus, Pen } from 'lucide-react';
import { LoaderCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import SectionCard from '../SectionCard';
import { SummaryTooltip } from '../TooltipModals';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Button } from '@/components/atoms/new-button';
import { Skeleton } from '@/components/atoms/Skeleton';
import Modal from '@/components/molecules/Modals/CustomModal';
import { TextWithReferences } from '@/helpers/formatReferences';

interface CaseSummarySectionProps {
  summaryText: string;
  isDeletedDocRunOngoing: boolean;
  legalIssues: string;
  isLoading?: boolean;
  caseId?: string;
  onExpandSummary?: () => void;
}

export const CaseSummarySection = ({
  summaryText,
  isDeletedDocRunOngoing = false,
  isLoading = false,
  caseId,
  legalIssues,
  onExpandSummary,
}: CaseSummarySectionProps) => {
  const [fullIssuesModalOpen, setFullIssuesModalOpen] = useState(false);
  const [tooltipModalOpen, setTooltipModalOpen] = useState(false);
  const [tooltipModalContent, setTooltipModalContent] = useState(<></>);
  const navigate = useNavigate();
  const openCaseSettingsDetails = () => {
    navigate(`/app/chronos/case-editor/settings/details?caseId=${caseId}`);
  };
  const openCaseSettingsDocuments = () => {
    navigate(`/app/chronos/case-editor/settings/documents?caseId=${caseId}`);
  };

  return (
    <>
      <div className="mb-6">
        <SectionCard
          title="Case Summary"
          onInfoClick={() => {
            setTooltipModalContent(<SummaryTooltip />);
            setTooltipModalOpen(true);
          }}
          headerActions={
            !isDeletedDocRunOngoing && !isLoading && summaryText ? (
              <button
                onClick={() => {
                  onExpandSummary?.();
                  trackCustomEvent(TrackingEvents.ViewFullSummaryOnSummary);
                }}
                className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
              >
                View Full Summary
                <ExternalLink className="h-3 w-3" />
              </button>
            ) : null
          }
        >
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full bg-gray-200" />
              <Skeleton className="h-4 w-5/6 bg-gray-200" />
              <Skeleton className="h-4 w-4/5 bg-gray-200" />
            </div>
          ) : isDeletedDocRunOngoing ? (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <LoaderCircle className="h-6 w-6 animate-spin text-gray-500" />
              <div className="text-sm text-gray-600">The summary is currently being updated.</div>
            </div>
          ) : summaryText ? (
            <div className="space-y-4">
              <div className="line-clamp-3 text-sm leading-relaxed text-gray-700">
                <TextWithReferences content={summaryText} />
              </div>

              {/* Case Issues Section */}

              <div className="border-t border-gray-100 pt-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex gap-4">
                    <Button
                      className="text-xs"
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        openCaseSettingsDocuments();
                        trackCustomEvent(TrackingEvents.AddDocumentsOnSummary);
                      }}
                    >
                      <FilePlus className="h-3 w-3" />
                      Add Documents
                    </Button>
                    <Button
                      className="text-xs"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        openCaseSettingsDetails();
                        trackCustomEvent(TrackingEvents.ChangeIssuesOnSummary);
                      }}
                    >
                      <Pen className="h-3 w-3" />
                      Change Issues
                    </Button>
                  </div>

                  <button
                    onClick={() => {
                      setFullIssuesModalOpen(true);
                      trackCustomEvent(TrackingEvents.ViewFullCaseIssuesOnSummary);
                    }}
                    className="inline-flex items-center gap-1 text-sm text-blue-500 underline hover:text-blue-700"
                  >
                    View Full Case Issues
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">No summary available</div>
          )}
        </SectionCard>
      </div>

      {/* Full Issues Modal */}
      <Modal
        maxWidth="68rem"
        handleClose={() => setFullIssuesModalOpen(false)}
        isOpen={fullIssuesModalOpen}
        content={
          <div className="p-6">
            <div className="mb-4">
              <h2 className="mb-2 text-xl font-semibold text-gray-900">Case Issues (Original)</h2>
              <div className="max-h-96 overflow-y-auto rounded-lg bg-gray-50 p-4">
                <div className="text-sm leading-relaxed whitespace-pre-line text-gray-700">{legalIssues}</div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                className="text-sm"
                size="sm"
                variant="primary"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.AddDocumentsOnSummary);
                  openCaseSettingsDocuments();
                  setFullIssuesModalOpen(false);
                }}
              >
                <FilePlus className="h-4 w-4" />
                Add Documents
              </Button>

              <Button
                className="text-sm"
                size="sm"
                variant="secondary"
                onClick={() => {
                  trackCustomEvent(TrackingEvents.ChangeIssuesOnSummary);
                  openCaseSettingsDetails();
                  setFullIssuesModalOpen(false);
                }}
              >
                <Pen className="h-4 w-4" />
                Change Issues
              </Button>
            </div>
          </div>
        }
      />

      {/* Tooltip Modal */}
      <Modal handleClose={() => setTooltipModalOpen(false)} isOpen={tooltipModalOpen} content={tooltipModalContent} />
    </>
  );
};
